import {Injectable} from '@angular/core';
import {HttpService} from '@app/core/services/http.service';
import {User} from '@app/core/models/user.model';
import {Shift} from '@app/core/models/shift.model';
import {AuthService} from '@app/core/services/auth.service';
import {RDate} from '@app/core/models/rDate.model';
import {Router} from '@angular/router';
import {Group} from '@app/core/models/group.model';
import {BaseService} from '@app/core/services/base.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {Observable} from 'rxjs';
import {ShiftDraft} from '@app/core/models/ShiftDraft.model';
import {map} from 'rxjs/operators';
import {Pagination, PaginationHolder} from '@app/core/models/pagination.model';

export interface IGetShiftResponse {
	shift: Shift;
	users_who_bid: User[];
	available_users: User[];
	approved_availabilities_user_id: number[];
	pending_availabilities_user_id: number[];
}

export interface IPostShiftParams {
	user_id: number;
	group_id: number | string;
	start_date: string;
	start_time: string;
	end_time: string;
	pause: number;
	note: string;
}

export interface IPatchShiftParams {
	user_id?: number;
	group_id?: number | string;
	start_date: string;
	start_time: string;
	end_time: string;
	pause?: number;
	note?: string;
	extra_shift?: boolean;
	approve_shift?: boolean;
}

export interface IUserShiftsWithTotalAmountResponse {
	shifts: Shift[];
	amount_of_shifts: number;
}

export interface IShiftFilter {
	has_punch_times?: boolean;
	punched_successful?: boolean;
	punched_within_start_end_times?: boolean;
	has_punch_note?: boolean;
	punched_within_punch_areas?: boolean;
	punched_with_location?: boolean;
	punched_out?: boolean;
}

interface IGetShiftsParams {
	start_date: string;
	end_date: string;
}

export interface IGetShiftsResponse {
	groups: Group[];
	shifts: Shift[];
	start_date: string;
	end_date: string;
}

@Injectable()
export class ShiftService extends BaseService {
	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		public router: Router,
		public store: Store<IAppState>,
	) {
		super(authService, interService, httpService, store);
	}

	public fresh(params?): Shift {
		if (!params) {
			return new Shift(this.tenantTZ, this.interService);
		}

		return new Shift(this.tenantTZ, this.interService).deserialize(params);
	}

	public getShifts(dates: IGetShiftsParams, shiftDraft?: ShiftDraft | undefined | null): Observable<IGetShiftsResponse> {
		// set 00:00:00 and 23:59:59 for the tenantTZ. As shifts are displayed in tenantTZ
		const firstDate = new RDate(this.tenantTZ, this.interService).deserialize(dates.start_date + ' 00:00:00', 'localDate', undefined, true);
		const lastDate = new RDate(this.tenantTZ, this.interService).deserialize(dates.end_date + ' 23:59:59', 'localDate', undefined, true);
		const dataTZFormatted = {
			start_date: firstDate.databaseDate,
			end_date: lastDate.databaseDate,
		};
		if (shiftDraft) {
			dataTZFormatted['shift_draft_id'] = shiftDraft.id;
		}

		return this.httpService.getJson<IGetShiftsResponse>('shifts', dataTZFormatted).pipe(map(res => {
			res.shifts = res.shifts.map(shift => new Shift(this.tenantTZ, this.interService).deserialize(shift));
			res.groups = res.groups.map(group => new Group(this.tenantTZ, this.interService).deserialize(group));

			return res;
		}));
	}

	public postShift(shift: IPostShiftParams, shiftDraft?: ShiftDraft | undefined | null): Observable<Shift> {
		const wholeDateStart = shift.start_date + ' ' + shift.start_time + ':00';
		const wholeDateEnd = shift.start_date + ' ' + shift.end_time + ':00';
		shift.start_date = new RDate(this.tenantTZ, this.interService).deserialize(wholeDateStart, 'localDate', undefined, true).UTCDate.format('YYYY-MM-DD');
		shift.start_time = new RDate(this.tenantTZ, this.interService).deserialize(wholeDateStart, 'localDate', undefined, true).UTCDate.format('HH:mm');
		shift.end_time = new RDate(this.tenantTZ, this.interService).deserialize(wholeDateEnd, 'localDate', undefined, true).UTCDate.format('HH:mm');
		if (!this.authService.gettingStartedState.finishedFirstShift) {
			shift['finished_first_shift'] = true;
		}
		if (shiftDraft) {
			shift['shift_draft_id'] = shiftDraft.id;
		}

		return this.httpService.postJson<{ shift: {} }>('shifts', shift)
			.pipe(map(res => {
				if (!this.authService.gettingStartedState.finishedFirstShift) {
					this.authService.gettingStartedState.finishedFirstShift = true;
					this.authService.gettingStartedState.justFinishedFirstShift = true;
					this.router.navigate(['./home/kom-i-gang']);
				}

				return new Shift(this.tenantTZ, this.interService).deserialize(res.shift);
			}));
	}

	public patchShift(id: number, shift: IPatchShiftParams, shiftDraft?: ShiftDraft | undefined | null): Observable<Shift> {
		const wholeDateStart = shift.start_date + ' ' + shift.start_time + ':00';
		const wholeDateEnd = shift.start_date + ' ' + shift.end_time + ':00';
		shift.start_date = new RDate(this.tenantTZ, this.interService).deserialize(wholeDateStart, 'localDate', undefined, true).UTCDate.format('YYYY-MM-DD');
		shift.start_time = new RDate(this.tenantTZ, this.interService).deserialize(wholeDateStart, 'localDate', undefined, true).UTCDate.format('HH:mm');
		shift.end_time = new RDate(this.tenantTZ, this.interService).deserialize(wholeDateEnd, 'localDate', undefined, true).UTCDate.format('HH:mm');
		if (shiftDraft) {
			shift['shift_draft_id'] = shiftDraft.id;
		}

		return this.httpService.patchJson<{ shift: {} }>('shifts/' + id, shift)
			.pipe(map(res => this.fresh(res.shift)));
	}

	public deleteShift(shift: Shift, shiftDraft?: ShiftDraft | undefined | null): Observable<Shift> {
		const data = {};
		if (shiftDraft) {
			data['shift_draft_id'] = shiftDraft.id;
		}

		return this.httpService.deleteJson('shifts/' + shift.id, data);
	}

	public getShift(id: number): Observable<IGetShiftResponse> {
		return this.httpService.getJson<any>('shifts/' + id)
			.pipe(map(res => {
				if (this.authService.currentUser.permission_level === 2) {
					res.users_who_bid = res.users_who_bid.map(user => new User(this.tenantTZ, this.interService).deserialize(user));
					res.available_users = res.available_users.map(user => new User(this.tenantTZ, this.interService).deserialize(user));
				}
				res.shift = new Shift(this.tenantTZ, this.interService).deserialize(res.shift);

				return res as IGetShiftResponse;
			}));
	}

	public getAvailableShifts(page_size): Observable<Shift[]> {
		return this.httpService.getJson<Shift[]>('shifts/onShiftStock', {page_size}).pipe(map((res: PaginationHolder<Shift>) => res.data.map(shift => new Shift(this.tenantTZ, this.interService).deserialize(shift))));
	}

	public getUserShifts(userId: number): Observable<Shift[]> {
		return this.httpService.getJson<Shift[]>('user/' + userId + '/shifts').pipe(map(shifts => shifts.map(shift => new Shift(this.tenantTZ, this.interService).deserialize(shift))));
	}

	public pendingApprovalShifts(shiftFilter: { only_include_payment_period: boolean }, page_size: number = 200, page: number = 1): Observable<PaginationHolder<Shift>> {
		const data = {
			...shiftFilter,
			page_size,
			page,
		};

		return this.httpService.getJson<PaginationHolder<Shift>>('actions/shiftsPendingApprove', data).pipe(map((res: any) => {
			res.data = res.data.map(shift => {
				const formattedShift = new Shift(this.tenantTZ, this.interService).deserialize(shift);
				formattedShift['startTime'] = (formattedShift.rounded_punch_start)
					? formattedShift.rounded_punch_start.localDate.format('HH:mm')
					: formattedShift.getStart().localDate.format('HH:mm');
				formattedShift['endTime'] = (formattedShift.rounded_punch_end)
					? formattedShift.rounded_punch_end.localDate.format('HH:mm')
					: formattedShift.getEnd().localDate.format('HH:mm');

				return formattedShift;
			});
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public addShiftToStock(shiftId: number): Observable<any> {
		return this.httpService.postJson('shifts/' + shiftId + '/shiftStock', {});
	}

	public removeShiftFromStock(shiftId: number): Observable<any> {
		return this.httpService.deleteJson('shifts/' + shiftId + '/shiftStock');
	}

	public addShiftStockBid(shiftId: number): Observable<any> {
		return this.httpService.postJson('shiftStockBids', {shift_id: shiftId});
	}

	public removeShiftStockBid(shiftId: number): Observable<any> {
		return this.httpService.deleteJson('shiftStockBids', {shift_id: shiftId});
	}

	public deleteInInterval(start: string, end: string, shiftDraft?: ShiftDraft | undefined | null): Observable<any> {
		// set 00:00:00 and 23:59:59 for the tenantTZ. As shifts are displayed in tenantTZ
		const firstDate = new RDate(this.tenantTZ, this.interService).deserialize(start + ' 00:00:00', 'localDate', undefined, true);
		const lastDate = new RDate(this.tenantTZ, this.interService).deserialize(end + ' 23:59:59', 'localDate', undefined, true);
		const dataTZFormatted = {
			start_date: firstDate.databaseDate,
			end_date: lastDate.databaseDate,
		};
		if (shiftDraft) {
			dataTZFormatted['shift_draft_id'] = shiftDraft.id;
		}

		return this.httpService.deleteJson('shifts/interval', dataTZFormatted);
	}

	public massShiftApprove(payload: { id: number, start_date: string, start_time: string, end_time: string }[]): Observable<{ updated_shifts: Shift[], skipped_shifts: Shift[] }> {
		payload = payload.map(s => {
			const wholeDateStart = s.start_date + ' ' + s.start_time + ':00';
			const wholeDateEnd = s.start_date + ' ' + s.end_time + ':00';
			s.start_date = new RDate(this.tenantTZ, this.interService).deserialize(wholeDateStart, 'localDate', undefined, true).UTCDate.format('YYYY-MM-DD');
			s.start_time = new RDate(this.tenantTZ, this.interService).deserialize(wholeDateStart, 'localDate', undefined, true).UTCDate.format('HH:mm');
			s.end_time = new RDate(this.tenantTZ, this.interService).deserialize(wholeDateEnd, 'localDate', undefined, true).UTCDate.format('HH:mm');

			return s;
		});

		return this.httpService.patchJson<{ updated_shifts: Shift[], skipped_shifts: Shift[] }>('shifts/batch', {shifts: payload}).pipe(map((res: any) => ({
			updated_shifts: res.updated_shifts.map(s => this.fresh(s)),
			skipped_shifts: res.skipped_shifts.map(s => this.fresh(s)),
		})));
	}

	public massShiftDelete(payload: { id: number }[]): Observable<any> {
		return this.httpService.deleteJson('shifts/batch', {shifts: payload});
	}
}
